/**
 * 帳款報表「列印 / 匯出 PDF」的預設檔名(REQ-20260831003)。
 *
 * 這一頁的列印走瀏覽器 `window.print()`,沒有引進 PDF 套件(REQ-20260806003
 * 起的既有決策),而**存檔對話框的預設檔名就是分頁標題** —— 所以做法是設
 * `document.title`(REQ-20260811002 在訂單列印頁定案的同一招)。
 *
 * 這裡只負責把畫面上的搜尋條件組成那個字串,抽成純函式是為了能單獨測。
 * 檔名不加 `.pdf`:瀏覽器存 PDF 時會自己補副檔名,自己加會變成 `xxx.pdf.pdf`。
 */
import type { PartyInfo } from '@app/shared';
import { sanitizeFilename } from '../../lib/filename';

interface ReportFilenameInput {
  /** 下拉選單顯示的報表名稱(例:帳款明細表) */
  reportLabel: string;
  filter: 'range' | 'range+person' | 'date';
  /**
   * 該報表有沒有客戶挑選器。沒有的報表整段省略 ——
   * 它們根本沒有客戶條件,寫「全部客戶」是假資訊。
   */
  hasCustomerFilter: boolean;
  customer: PartyInfo | null;
  from: string;
  to: string;
  date: string;
}

/** 只帶簡稱,不帶客戶編號(REQ-20260901001 改;原本是簡稱 + `_` + 編號) */
function customerSegment(customer: PartyInfo | null): string {
  if (!customer) return '全部客戶';
  return customer.simpleName;
}

/**
 * 日期一律用字串處理 —— `<input type="date">` 的值本來就是使用者看到的那一天,
 * 丟進 `new Date()` 再格式化只會因時區差跑掉一天(CLAUDE.md §6.1)。
 */
function dateSegment(input: ReportFilenameInput): string {
  if (input.filter === 'date') return input.date.replaceAll('-', '');
  const from = input.from.replaceAll('-', '');
  const to = input.to.replaceAll('-', '');
  if (from && to) return `${from}-${to}`;
  if (from) return `${from}起`;
  if (to) return `至${to}`;
  return '全部日期';
}

export function buildReportFilename(input: ReportFilenameInput): string {
  const segments = [input.reportLabel];
  if (input.hasCustomerFilter) segments.push(customerSegment(input.customer));
  segments.push(dateSegment(input));
  return sanitizeFilename(segments.filter((s) => s.length > 0).join('_'), '報表');
}
